import { $, $$, on, doc } from './util.js';

/* 12. MOBILE NAV MENU
   The hamburger (#navBurger) opens the panel (#navMobile) that carries the same
   links as the collapsed .nav-links. Below 880px the bar has no room for them —
   see the media steps in scss/components/_nav.scss — so this is the only route
   to the menu at that size.

   `hidden` is removed a frame before .open is added, because a transition cannot
   run on an element that is still display:none. Closing does the reverse: drop
   .open, then re-apply `hidden` once the transition has finished, so the panel
   is properly out of the tab order rather than merely invisible. */
var BREAKPOINT = 880;

export function initNavMenu() {
	var btn = $('#navBurger');
	var panel = $('#navMobile');
	if (!btn || !panel) { return; }

	var closeTimer = null;

	function isOpen() {
		return btn.getAttribute('aria-expanded') === 'true';
	}

	function open() {
		if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
		panel.hidden = false;
		btn.setAttribute('aria-expanded', 'true');
		// Next frame, so the browser has a painted starting state to animate from.
		requestAnimationFrame(function () { panel.classList.add('open'); });
	}

	function close() {
		btn.setAttribute('aria-expanded', 'false');
		panel.classList.remove('open');
		closeTimer = setTimeout(function () { panel.hidden = true; closeTimer = null; }, 220);
	}

	function toggle() {
		isOpen() ? close() : open();
	}

	on(btn, 'click', function (e) {
		e.preventDefault();
		toggle();
	});

	// Follow a link and the panel has done its job.
	$$('a', panel).forEach(function (link) {
		on(link, 'click', close);
	});

	on(doc, 'keydown', function (e) {
		if (e.key === 'Escape' && isOpen()) {
			close();
			btn.focus();
		}
	});

	on(doc, 'click', function (e) {
		if (!isOpen()) { return; }
		if (panel.contains(e.target) || btn.contains(e.target)) { return; }
		close();
	});

	// Widen the window past the breakpoint and the links are back on the bar;
	// leaving the panel open would show the menu twice.
	on(window, 'resize', function () {
		if (isOpen() && window.innerWidth > BREAKPOINT) { close(); }
	});
}

/* MEGA MENU
   Each top-level nav item that carries a panel is wrapped in .nl-sub-wrap--mega
   (elements/menu/nav-links.html) with a [data-mega-trigger] link and a
   .mega-panel (elements/menu/mega-panel.html). CSS opens the panel on :hover and
   :focus-within; this adds the click/keyboard contract on top:
     - .nav-wrap gets data-mega-js="on" so the stylesheet can hand over control;
     - clicking the chevron (.nl-sub-chev) toggles the panel and keeps the link
       from navigating; clicking the link itself navigates and closes the rest;
     - hovering one wrap closes any other that was toggled open;
     - Escape closes an open panel and returns focus to its trigger;
     - a click outside, or on a link inside the panel, closes it.

   Recovered 2026-10-13 from the dev server's compiled dist/app.js (branch
   origin/dev-server-snapshot-2026-10-07); the source for it was never committed. */
export function initMegaMenu() {
	var wraps = $$('.nl-sub-wrap--mega');
	if (!wraps.length) { return; }

	var navWrap = $('.nav-wrap');
	if (navWrap) { navWrap.setAttribute('data-mega-js', 'on'); }

	function trigger(wrap) {
		return wrap.querySelector('[data-mega-trigger]');
	}

	function isOpen(wrap) {
		return wrap.classList.contains('is-open');
	}

	function setOpen(wrap, open) {
		var t = trigger(wrap);
		if (open) { wrap.classList.add('is-open'); } else { wrap.classList.remove('is-open'); }
		if (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); }
	}

	function closeOthers(except) {
		wraps.forEach(function (wrap) {
			if (wrap !== except) { setOpen(wrap, false); }
		});
	}

	wraps.forEach(function (wrap) {
		var t = trigger(wrap);
		if (!t) { return; }

		on(wrap, 'mouseenter', function () { closeOthers(wrap); });

		on(t, 'click', function (e) {
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) { return; }
			var onChevron = e.target && e.target.closest ? e.target.closest('.nl-sub-chev') : null;
			if (onChevron) {
				e.preventDefault();
				var next = !isOpen(wrap);
				closeOthers(wrap);
				setOpen(wrap, next);
			} else {
				closeOthers(null);
			}
		});
	});

	on(doc, 'keydown', function (e) {
		if (e.key !== 'Escape' && e.keyCode !== 27) { return; }
		wraps.forEach(function (wrap) {
			if (!isOpen(wrap)) { return; }
			var t = trigger(wrap);
			setOpen(wrap, false);
			if (t) { t.focus(); }
		});
	});

	on(doc, 'click', function (e) {
		wraps.forEach(function (wrap) {
			if (isOpen(wrap) && !wrap.contains(e.target)) { setOpen(wrap, false); }
		});
	});

	on(doc, 'click', function (e) {
		var inPanel = e.target.closest ? e.target.closest('.mega-panel a') : null;
		if (inPanel) { closeOthers(null); }
	});
}
